import React from 'react';
import moment from 'moment';
import { evalJsCode, getUserFullName } from 'utils';
import {
  FormData,
  FormInput,
  TypedFormInputs,
  UseFormMethods,
  getFormInputsFromTyped,
} from 'components/form';
import {
  useGetAvailableJobsiteWorkersQuery,
  UserRoleKey,
} from 'apollo/generated/client-operations';
import {
  AvailableJobsiteWorkerOption,
  AvailableJobsiteWorkerOptions,
  ContextSelectOptions,
  CustomFormDependency,
  CustomFormInputs,
  EditFormData,
  FormContext,
  FormSubmissionWorkerAssociationType,
  JobsiteFeaturesModule,
  JobsiteFormSubmission,
  JobsiteModule,
  WatchedFieldConfig,
  WatchedFormStateConfig,
} from './types';

export const getFeaturesModule = (
  modules: JobsiteModule[],
): JobsiteFeaturesModule =>
  modules?.find((m): m is JobsiteFeaturesModule => {
    return m.__typename === 'JobsiteFeaturesModule'; // eslint-disable-line no-underscore-dangle
  });

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const installedModules = __webpack_module_cache__; // eslint-disable-line camelcase
const modulesCache: Record<string, unknown> = {};

function requireModule(moduleSpecifier: string): unknown {
  const moduleNames = Object.keys(installedModules);
  const modulePatterns = [
    `^./${moduleSpecifier}.tsx?$`,
    `^./${moduleSpecifier}/index.tsx?$`,
    `^../node_modules/${moduleSpecifier}.[jt]sx?$`,
    `^../node_modules/${moduleSpecifier}/index.[jt]sx?$`,
    `^../node_modules/${moduleSpecifier}/${moduleSpecifier}.[jt]sx?$`,
    `^../node_modules/${moduleSpecifier}/dist/index.[jt]s$`,
  ].map((pattern) => new RegExp(pattern));

  const moduleId = moduleNames.find((m) =>
    modulePatterns.some((pattern) => m.match(pattern)),
  );
  const installedModule = installedModules[moduleId]?.exports;
  if (!installedModule) {
    // eslint-disable-next-line no-console
    console.error(
      `Module "${moduleSpecifier}" with moduleId "${moduleSpecifier}" could not be resolved`,
    );
  }
  return installedModule;
}

const resolveModule = (moduleSpecifier: string, moduleId: string): unknown => {
  if (modulesCache[moduleSpecifier]) {
    return modulesCache[moduleSpecifier];
  }

  if (moduleId && installedModules[moduleId]) {
    const installedModule = installedModules[moduleId]?.exports;
    modulesCache[moduleSpecifier] = installedModule;
    return installedModule;
  }

  const installedModule = requireModule(moduleSpecifier);
  modulesCache[moduleSpecifier] = installedModule;
  return installedModule;
};

const getDependencies = async (
  dependenciesExpressions: CustomFormDependency[],
): Promise<Record<string, unknown>> => {
  const modules = Object.fromEntries(
    dependenciesExpressions.map(({ moduleSpecifier, moduleId }) => {
      return [moduleSpecifier, resolveModule(moduleSpecifier, moduleId)];
    }),
  );
  const dependenciesCode = [
    ...dependenciesExpressions.map(({ moduleSpecifier, importClause }) => {
      return `const ${importClause} = modules['${moduleSpecifier}'];`;
    }),
    `return {
      ${dependenciesExpressions
        .map(({ importClause }) =>
          importClause.includes('{')
            ? `...${importClause},`
            : `${importClause},`,
        )
        .join('\n')}
    }`,
  ].join('\n');

  const dependencies = evalJsCode<Record<string, unknown>>(dependenciesCode, {
    type: 'code',
    modules,
  });
  return Promise.resolve(dependencies);
};

export const useDependencies = (
  dependenciesExpressions: CustomFormDependency[],
): Record<string, unknown> | null => {
  const stringifiedDependencies = JSON.stringify(dependenciesExpressions);
  const [dependencies, setDependencies] = React.useState<Record<
    string,
    unknown
  > | null>(null);

  React.useEffect(() => {
    if (dependenciesExpressions) {
      getDependencies(dependenciesExpressions).then(setDependencies);
    } else if (dependencies !== null) {
      // reset dependencies to null when dependenciesExpressions was reset
      setDependencies(null);
    }
  }, [stringifiedDependencies]);

  return dependencies;
};

export const useDynCtx = (formContent: any): Record<string, unknown> => {
  const { dynCtx: dynCtxExpression, dependencies: dependenciesExpressions } =
    (formContent?.modal.dynCtx ? formContent?.modal : formContent?.edit) ?? {};

  const dependencies = useDependencies(dependenciesExpressions);

  return React.useMemo(() => {
    return (
      dynCtxExpression &&
      dependencies &&
      evalJsCode(dynCtxExpression, { ctx: { dependencies } })
    );
  }, [dependencies, dynCtxExpression]);
};

/**
 * This custom hook is used to watch for changes in form fields or
 * form dropdown options and trigger actions accordingly.
 */
export const useWatchedFields = <
  TFormData extends EditFormData | FormData,
  TWatchedField extends CustomFormInputs<TFormData> | ContextSelectOptions,
>(
  watchedFieldsConfig: WatchedFieldConfig<TWatchedField>[],
  watched: Partial<TWatchedField>,
  evalContext: { ctx: FormContext<TFormData> },
): void => {
  const prevWatchedRef = React.useRef(watched);

  React.useEffect(() => {
    if (!evalContext.ctx.form.formState.isDirty) {
      prevWatchedRef.current = watched;
      // If the form is not dirty, we don't need to check for changes in watched fields
      return;
    }

    watchedFieldsConfig
      ?.filter(
        (
          item,
        ): item is Extract<
          WatchedFieldConfig<CustomFormInputs<TFormData>>,
          { field: string }
        > => typeof item === 'object',
      )
      .forEach(({ field, action: actionExpression }) => {
        const currentValue = watched[field];
        const prevValue = prevWatchedRef.current?.[field];
        if (currentValue !== prevValue) {
          prevWatchedRef.current[field] = currentValue;
          if (actionExpression && evalContext) {
            evalJsCode(actionExpression, evalContext);
          }
        }
      });
  }, [...Object.values(watched)]);
};

export const useWatchedFormState = <TFormData extends EditFormData | FormData>(
  watchedFormStateConfig: WatchedFormStateConfig<CustomFormInputs<TFormData>>[],
  formState: UseFormMethods<CustomFormInputs<TFormData>>['formState'],
  evalContext: { ctx: FormContext<TFormData> },
): void => {
  const {
    isDirty,
    dirtyFields,
    isSubmitted,
    isSubmitSuccessful,
    submitCount,
    touched,
    isSubmitting,
    isValidating,
    isValid,
    errors,
  } = formState;
  const prevWatchedRef = React.useRef({
    isDirty,
    dirtyFields,
    isSubmitted,
    isSubmitSuccessful,
    submitCount,
    touched,
    isSubmitting,
    isValidating,
    isValid,
    errors,
  });

  React.useEffect(() => {
    watchedFormStateConfig?.forEach(({ field, action: actionExpression }) => {
      const currentValue = formState[field];
      const prevValue = prevWatchedRef.current?.[field];
      if (currentValue !== prevValue) {
        Object.assign(prevWatchedRef.current, { [field]: currentValue });
        if (actionExpression && evalContext) {
          evalJsCode(actionExpression, evalContext);
        }
      }
    });
  }, [
    isDirty,
    dirtyFields,
    isSubmitted,
    isSubmitSuccessful,
    submitCount,
    touched,
    isSubmitting,
    isValidating,
    isValid,
    errors,
  ]);
};

type UseAvailableJobsiteWorkerOptionsArgs = {
  jobsiteFormSubmission: JobsiteFormSubmission;
  jobsiteId?: string;
  skip?: boolean;
};

type UseAvailableJobsiteWorkerOptionsResult = {
  availableJobsiteWorkerOptions: AvailableJobsiteWorkerOptions;
  loading: boolean;
};

export const createAvailableJobsiteWorkerOptions = (args?: {
  jobsiteFormSubmission?: JobsiteFormSubmission;
  items?: AvailableJobsiteWorkerOption[];
}): AvailableJobsiteWorkerOptions => {
  const { jobsiteFormSubmission, items } = args ?? {};
  const availableOptionsByAssociationType: Record<
    string,
    AvailableJobsiteWorkerOption[]
  > = {};

  return {
    jobsiteFormSubmission,
    items,
    byAssociationType(
      associationType: FormSubmissionWorkerAssociationType,
    ): AvailableJobsiteWorkerOption[] {
      if (
        !availableOptionsByAssociationType[associationType] &&
        items?.length
      ) {
        const jfsWorkersMap = Object.fromEntries(
          jobsiteFormSubmission?.jobsiteWorkers.edges
            .filter(({ node }) => node.associationType === associationType)
            .map(({ node }) => [node.jobsiteWorker.jobsiteWorkerId, node]) ??
            [],
        );
        availableOptionsByAssociationType[associationType] = items.map(
          (opt): AvailableJobsiteWorkerOption => {
            const { id, extraData } =
              jfsWorkersMap[opt.jobsiteWorker.jobsiteWorkerId] ?? {};
            return { ...opt, associationType, id, extraData };
          },
        );
      }
      return availableOptionsByAssociationType[associationType] ?? [];
    },
  };
};

export const useAvailableJobsiteWorkerOptions = (
  args: UseAvailableJobsiteWorkerOptionsArgs,
): UseAvailableJobsiteWorkerOptionsResult => {
  const { jobsiteFormSubmission, skip } = args;

  const { jobsiteForm, jobsiteContractors, startAt } =
    jobsiteFormSubmission ?? {};
  const jobsiteId = jobsiteForm?.jobsite.jobsiteId ?? args.jobsiteId;
  const contractorIds = jobsiteContractors?.edges
    .filter(({ node }) => node.associationType === 'assignment')
    .map(({ node }) => node.jobsiteContractor.contractor.contractorId);

  const { data, loading } = useGetAvailableJobsiteWorkersQuery({
    fetchPolicy: 'no-cache',
    skip: skip || !jobsiteId,
    variables: {
      jobsiteId,
      jobsiteJobsiteWorkersInput: {
        contractorIds,
        onSiteDate: startAt ? moment.utc(startAt).toDate() : null,
      },
    },
  });

  const availableJobsiteWorkerOptions = React.useMemo(() => {
    const items =
      data?.getJobsite.jobsiteWorkers.edges.map<AvailableJobsiteWorkerOption>(
        ({ node: jw }) => ({
          value: jw.jobsiteWorkerId,
          label: getUserFullName(jw.contractorWorker.worker.user),
          jobsiteWorker: jw,
          id: undefined,
          associationType: undefined,
          extraData: undefined,
          hasAJobsiteRole: !!jw.contractorWorker.worker.user.roles.length,
          hasRole: (role: UserRoleKey): boolean => {
            return jw.contractorWorker.worker.user.roles.some(
              (r) => r.key === role,
            );
          },
        }),
      );
    return skip || items
      ? createAvailableJobsiteWorkerOptions({ jobsiteFormSubmission, items })
      : undefined;
  }, [data, jobsiteFormSubmission, skip]);

  return {
    loading,
    availableJobsiteWorkerOptions,
  };
};

/**
 * It converts a form inputs structure from a map to an array one.
 * @param formInputs Inputs disposed in a tree structure where the key is the name of the component.
 * @returns form inputs structured as arrays
 */
export const formInputsAsArray = <TFormData extends EditFormData | FormData>(
  formInputs: TypedFormInputs<TFormData>,
): FormInput<CustomFormInputs<TFormData>>[] => {
  const inputs = getFormInputsFromTyped(formInputs);
  return inputs?.map((input) => {
    const { children, ...restInput } = input;
    if (children) {
      return {
        ...restInput,
        children: formInputsAsArray(children as TypedFormInputs<TFormData>),
      };
    }
    return input;
  }) as unknown as FormInput<CustomFormInputs<TFormData>>[];
};
